import {
  Space,
  SpaceAmenity,
  SpaceImage,
  SpacePreview,
  SpacePreviewScreen,
  SpaceAnalyticsGroup,
  SpaceAnalyticsMetric,
  SpaceAnalyticsValues,
  SpaceRegion,
  SpaceScreen,
  SpaceScreenGroup,
  SpaceStaff,
} from "services/entities/spaces";
import { callAPI } from "services/api";
import { CurrencyAmount } from "util/currency";
import { getCacheItem, setCacheItem } from "util/cache";
import { DateTime } from "util/time";
import { Vector3D } from "util/geometry";

export function readSpaceScreen(data: any): SpaceScreen {
  const { x, y, width, height, ...props } = data;
  return {
    bounds: { x: x, y: y, width: width, height: height },
    ...props,
  };
}

export function readSpaceScreenGroup(data: any): SpaceScreenGroup {
  const { is_design_hero, x, y, width, height, resolution, screens, ...props } =
    data;
  return {
    isDesignHero: is_design_hero,
    bounds: { x: x, y: y, width: width, height: height },
    resolution: { width: resolution.x, height: resolution.y },
    screens: screens.map(readSpaceScreen),
    ...props,
  };
}

function readSpaceRegion(data: any): SpaceRegion {
  const { screen_groups, width, height, ...fields } = data;
  return {
    screenGroups: screen_groups.map(readSpaceScreenGroup),
    size: { width: width, height: height },
    ...fields,
  };
}

const SPACES_CACHE_KEY = "spaces";

function readSpaceImage(data: any): SpaceImage {
  return {
    type: data.type,
    url: data.image,
  };
}

function readSpaceAmenity(data: any): SpaceAmenity {
  return data;
}

function readSpaceStaff(data: any): SpaceStaff {
  return {
    name: data.name,
    title: data.title,
    imageURL: data.image,
  };
}

export function readSpace(data: any): Space {
  const {
    regions,
    short_address,
    short_description,
    map_url,
    map_image,
    floor_area,
    footfall_period,
    sample_price,
    hero_image,
    status,
    images,
    amenities,
    staff,
    tour_description,
    tour_url,
    previewer_enabled,
    designer_enabled,
    booking_enabled,
    is_design_hero,
    page_title,
    meta_description,
    ...fields
  } = data;
  return {
    regions: regions ? regions.map(readSpaceRegion) : [],
    shortAddress: short_address,
    shortDescription: short_description,
    mapURL: map_url,
    mapImageURL: map_image,
    tourDescription: tour_description,
    tourURL: tour_url ? new URL(tour_url) : null,
    floorArea: floor_area,
    footfallPeriod: footfall_period,
    samplePrice: sample_price ? new CurrencyAmount(sample_price) : null,
    heroImage: hero_image,
    status: status,
    previewerEnabled: previewer_enabled,
    designerEnabled: designer_enabled,
    bookingEnabled: booking_enabled,
    images: images ? images.map(readSpaceImage) : [],
    amenities: amenities ? amenities.map(readSpaceAmenity) : [],
    staff: staff ? staff.map(readSpaceStaff) : [],
    isDesignHero: is_design_hero,
    pageTitle: page_title,
    metaDescription: meta_description,
    ...fields,
  };
}

export async function loadSpaces(): Promise<Space[]> {
  const cachedSpaces = getCacheItem(SPACES_CACHE_KEY);
  if (cachedSpaces) {
    return cachedSpaces;
  }
  const response = await callAPI("/spaces/", "GET");
  const spaces = response.map(readSpace);
  setCacheItem(SPACES_CACHE_KEY, spaces);
  return spaces;
}

export async function loadSpace(code: string): Promise<Space> {
  const spaces = await loadSpaces();
  const space = spaces.find((s) => s.code === code);
  if (!space) {
    throw new Error("Space not found");
  }
  return space;
}

function readVector3D(data: any): Vector3D {
  if (!data) {
    return { x: 0, y: 0, z: 0 };
  } else {
    return {
      x: data.x || 0,
      y: data.y || 0,
      z: data.z || 0,
    };
  }
}

function readSpacePreviewScreen(data: any): SpacePreviewScreen {
  const transform = data.transform;
  return {
    code: data.screen_group,
    size: { width: data.size.x, height: data.size.y },
    position: readVector3D(transform?.position),
    rotation: readVector3D(transform?.rotation),
  };
}

function readSpacePreview(data: any): SpacePreview {
  const camera = data.camera_transform;
  return {
    name: data.name,
    background: new URL(data.background),
    foreground: data.foreground ? new URL(data.foreground) : null,
    camera: {
      position: readVector3D(camera?.position),
      rotation: readVector3D(camera?.rotation),
      fov: camera?.fov || 20,
    },
    screens: data.screens.map(readSpacePreviewScreen),
  };
}

export async function loadSpacePreviews(code: string): Promise<SpacePreview[]> {
  const response = await callAPI(`/spaces/${code}/previews/`, "GET");
  return response.map(readSpacePreview);
}

export function readSpaceAnalyticsValues(data: any): SpaceAnalyticsValues {
  const values = new Map<SpaceAnalyticsMetric, number>();
  for (const metric of Object.keys(data)) {
    values.set(metric as SpaceAnalyticsMetric, data[metric]);
  }
  return values;
}

export function readSpaceAnalyticsGroup(data: any): SpaceAnalyticsGroup {
  return {
    startTime: DateTime.parse(data.start_time),
    endTime: DateTime.parse(data.end_time),
    values: readSpaceAnalyticsValues(data.values),
  };
}
